import { Map } from 'immutable';

const charsToReplace = /[^\w]/g;

/**
 * this function can be used to instantiate a local variable in an action file that can represent the action types that are commonly used with a paging display
 *
 * the types of actions that are supported by this are as follows:
 * - page: this is an action that changes the currently selected page in the reducer
 * - perPage: this is an action that changes the current number of items that should be displayed on an individual page
 * - sort: this is an action that changes how a paged list is sorted
 * @param {string} namespace This string corresponds to the namespace associated with the actions file that calls this
 * @param {string} actionPrefix This string represents provides a way to distinguish between multiple different pagingActions in the same namespace
 * @returns a structure that contains the action type names, an array of all the action type names, and an actionTypes object has the action type names
 *          associated with variable names that are in standard Participant Portal format for the actionTypes structure
 */
export const pagingActions = (namespace, actionPrefix) => {
  const changePageValue = `${namespace}.${actionPrefix}-change-page-number`;
  const changePerPageValue = `${namespace}.${actionPrefix}-change-per-page-number`;
  const changeSortValue = `${namespace}.${actionPrefix}-change-sort`;

  const changePageName = `${actionPrefix}-change-page-number`.replace(charsToReplace, "_").toUpperCase();
  const changePerPageName = `${actionPrefix}-change-per-page-number`.replace(charsToReplace, "_").toUpperCase();
  const changeSortName = `${actionPrefix}-change-sort`.replace(charsToReplace, "_").toUpperCase();

  return {
    page: changePageValue,
    perPage: changePerPageValue,
    sort: changeSortValue,
    allTypes: [ changePageValue, changePerPageValue, changeSortValue ],
    actionTypes: {
      [changePageName]: changePageValue,
      [changePerPageName]: changePerPageValue,
      [changeSortName]: changeSortValue,
    }
  }
}

/**
 * this function dispatches a standardized action for changing the page number of a paged list display
 * @param {number} page the page number to set in the reducer
 * @param {object} actions an object expected to be in the format that is produced by the pagingActions function
 * @returns an action formatted in the way that the paging reducer expects
 */
export const changePage = (page, actions) => {
  return {
    type : actions.page,
    page
  }
}

/**
 * this function dispatches a standardized action for changing the number of items that should be displayed on one page of a paged list
 * @param {number} perPage the number of items per page to set in the reducer
 * @param {object} actions an object expected to be in the format that is produced by the pagingActions function
 * @returns an action formatted in the way that the paging reducer expects
 */
export const changePerPage = (perPage, actions) => {
  return {
    type : actions.perPage,
    perPage
  }
}

const changeSortStart = (sortBy, sortAsc, actions) => {
  return {
    type: actions.sort,
    sortBy,
    sortAsc
  }
}

/**
 * this function dispatches a standardized action for managing how a paged list is sorted,
 * this manages both what aspect of the list is being sorted and whether or not it should be sorted in ascending or descending order
 * and a path that corresponds to the area of the state where the current sorting information is stored
 * @param {string} newSort this is the aspect of the list to base the sorting on, if this is area that it is already being sorted by then it will flip to the opposite sorting order
 * @param {object} actions an object expected to be in the format that is produced by the pagingActions function
 * @param {array} path the path in the state where the paging information is located, the expected format is [ "section1", "section2", ...]
 * @returns an action formatted in the way that the paging reducer expects
 */
export const changeSort = (newSort, actions, path) => {
  return (dispatch, getState) => {
    const state = new Map(getState());
    const pagingState = state.getIn(path);
    const { sortBy, sortAsc } = pagingState || {};
    if(sortBy === newSort)
      dispatch(changeSortStart(newSort, !sortAsc, actions));
    else
      dispatch(changeSortStart(newSort, true, actions));
  }
}


